import produce from 'immer';
import { types } from './actions';
import type {
    IBuzzbridgeLoyaltyDashboard,
    IBuzzbridgeMemberDetail,
    IBuzzbridgeNetworkInvite,
    IBuzzbridgeNetworkResponse,
    IBuzzbridgeRedemptionProgram,
    IBuzzbridgeRedemptionResult,
    IBuzzbridgeLoyaltyWalletResponse,
    IBuzzbridgeLoyaltyWithdrawal,
    IAdminLoyaltyGrantResult,
    IAdminLoyaltyOverview,
    IAdminLoyaltyLeaderboardResponse,
    IAdminLoyaltyUserDetail,
} from '../../types/global/buzzbridgeLoyalty.types';

export interface IBuzzbridgeLoyaltyState {
    dashboard?: IBuzzbridgeLoyaltyDashboard | null;
    fetchingDashboard: boolean;
    dashboardError: unknown;

    network?: IBuzzbridgeNetworkResponse | null;
    fetchingNetwork: boolean;
    networkError: unknown;

    memberDetail?: IBuzzbridgeMemberDetail | null;
    fetchingMember: boolean;
    memberError: unknown;

    invite?: IBuzzbridgeNetworkInvite | null;
    fetchingInvite: boolean;

    redemptionCatalog: IBuzzbridgeRedemptionProgram[];
    fetchingCatalog: boolean;

    redeemingProgramId: string | null;
    lastRedemption: IBuzzbridgeRedemptionResult | null;

    wallet?: IBuzzbridgeLoyaltyWalletResponse | null;
    fetchingWallet: boolean;
    walletError: unknown;

    requestingWithdrawal: boolean;
    withdrawError: unknown;

    adminWithdrawals: IBuzzbridgeLoyaltyWithdrawal[];
    fetchingAdminWithdrawals: boolean;
    adminWithdrawalsError: unknown;
    patchingAdminWithdrawalId: string | null;

    grantingLoyalty: boolean;
    adminGrantResult: IAdminLoyaltyGrantResult | null;

    adminOverview?: IAdminLoyaltyOverview | null;
    fetchingAdminOverview: boolean;

    adminLeaderboard?: IAdminLoyaltyLeaderboardResponse | null;
    fetchingAdminLeaderboard: boolean;

    adminUserDetail?: IAdminLoyaltyUserDetail | null;
    fetchingAdminUser: boolean;
    adminUserError: unknown;
}

const initialState: IBuzzbridgeLoyaltyState = {
    fetchingDashboard: false,
    dashboardError: null,
    fetchingNetwork: false,
    networkError: null,
    fetchingMember: false,
    memberError: null,
    fetchingInvite: false,
    redemptionCatalog: [],
    fetchingCatalog: false,
    redeemingProgramId: null,
    lastRedemption: null,
    fetchingWallet: false,
    walletError: null,
    requestingWithdrawal: false,
    withdrawError: null,
    adminWithdrawals: [],
    fetchingAdminWithdrawals: false,
    adminWithdrawalsError: null,
    patchingAdminWithdrawalId: null,
    grantingLoyalty: false,
    adminGrantResult: null,
    fetchingAdminOverview: false,
    fetchingAdminLeaderboard: false,
    fetchingAdminUser: false,
    adminUserError: null,
};

export function reducer(state = initialState, action: any): IBuzzbridgeLoyaltyState {
    return produce(state, draft => {
        switch (action.type) {
            case types.GET_LOYALTY_DASHBOARD:
                draft.fetchingDashboard = true;
                draft.dashboardError = null;
                break;
            case types.GET_LOYALTY_DASHBOARD_SUCCESS:
                draft.fetchingDashboard = false;
                draft.dashboard = action.payload;
                break;
            case types.GET_LOYALTY_DASHBOARD_FAIL:
                draft.fetchingDashboard = false;
                draft.dashboardError = action.payload;
                break;

            case types.GET_LOYALTY_NETWORK:
                draft.fetchingNetwork = true;
                draft.networkError = null;
                break;
            case types.GET_LOYALTY_NETWORK_SUCCESS:
                draft.fetchingNetwork = false;
                draft.network = action.payload;
                break;
            case types.GET_LOYALTY_NETWORK_FAIL:
                draft.fetchingNetwork = false;
                draft.networkError = action.payload;
                break;

            case types.GET_LOYALTY_MEMBER:
                draft.fetchingMember = true;
                draft.memberError = null;
                break;
            case types.GET_LOYALTY_MEMBER_SUCCESS:
                draft.fetchingMember = false;
                draft.memberDetail = action.payload;
                break;
            case types.GET_LOYALTY_MEMBER_FAIL:
                draft.fetchingMember = false;
                draft.memberError = action.payload;
                break;

            case types.GET_LOYALTY_INVITE:
                draft.fetchingInvite = true;
                break;
            case types.GET_LOYALTY_INVITE_SUCCESS:
                draft.fetchingInvite = false;
                draft.invite = action.payload;
                break;
            case types.GET_LOYALTY_INVITE_FAIL:
                draft.fetchingInvite = false;
                break;

            case types.GET_REDEMPTION_CATALOG:
                draft.fetchingCatalog = true;
                break;
            case types.GET_REDEMPTION_CATALOG_SUCCESS:
                draft.fetchingCatalog = false;
                draft.redemptionCatalog = action.payload ?? [];
                break;
            case types.GET_REDEMPTION_CATALOG_FAIL:
                draft.fetchingCatalog = false;
                break;

            case types.REDEEM_POINTS:
                draft.redeemingProgramId = action.payload?.program_id ?? null;
                break;
            case types.REDEEM_POINTS_SUCCESS:
                draft.redeemingProgramId = null;
                draft.lastRedemption = action.payload;
                if (draft.dashboard?.loyalty_points && action.payload?.points_spent) {
                    draft.dashboard.loyalty_points.balance = Math.max(
                        0,
                        draft.dashboard.loyalty_points.balance - action.payload.points_spent,
                    );
                    draft.dashboard.loyalty_points.lifetime_spent =
                        (draft.dashboard.loyalty_points.lifetime_spent ?? 0) + action.payload.points_spent;
                }
                break;
            case types.REDEEM_POINTS_FAIL:
                draft.redeemingProgramId = null;
                break;

            case types.GET_LOYALTY_WALLET:
                draft.fetchingWallet = true;
                draft.walletError = null;
                break;
            case types.GET_LOYALTY_WALLET_SUCCESS:
                draft.fetchingWallet = false;
                draft.wallet = action.payload;
                break;
            case types.GET_LOYALTY_WALLET_FAIL:
                draft.fetchingWallet = false;
                draft.walletError = action.payload;
                break;

            case types.REQUEST_LOYALTY_WITHDRAWAL:
                draft.requestingWithdrawal = true;
                draft.withdrawError = null;
                break;
            case types.REQUEST_LOYALTY_WITHDRAWAL_SUCCESS: {
                draft.requestingWithdrawal = false;
                const withdrawal = action.payload?.withdrawal;
                if (withdrawal && draft.wallet?.cash_wallet) {
                    draft.wallet.cash_wallet.pending_withdrawal_cents =
                        (draft.wallet.cash_wallet.pending_withdrawal_cents ?? 0) + withdrawal.amount_cents;
                }
                if (withdrawal && draft.dashboard?.cash_wallet) {
                    draft.dashboard.cash_wallet.pending_withdrawal_cents =
                        (draft.dashboard.cash_wallet.pending_withdrawal_cents ?? 0) + withdrawal.amount_cents;
                }
                break;
            }
            case types.REQUEST_LOYALTY_WITHDRAWAL_FAIL:
                draft.requestingWithdrawal = false;
                draft.withdrawError = action.payload;
                break;

            case types.GET_ADMIN_LOYALTY_WITHDRAWALS:
                draft.fetchingAdminWithdrawals = true;
                draft.adminWithdrawalsError = null;
                break;
            case types.GET_ADMIN_LOYALTY_WITHDRAWALS_SUCCESS:
                draft.fetchingAdminWithdrawals = false;
                draft.adminWithdrawals = action.payload?.withdrawals ?? [];
                break;
            case types.GET_ADMIN_LOYALTY_WITHDRAWALS_FAIL:
                draft.fetchingAdminWithdrawals = false;
                draft.adminWithdrawalsError = action.payload;
                break;

            case types.PATCH_ADMIN_LOYALTY_WITHDRAWAL:
                draft.patchingAdminWithdrawalId = action.payload?.withdrawalId ?? null;
                break;
            case types.PATCH_ADMIN_LOYALTY_WITHDRAWAL_SUCCESS: {
                draft.patchingAdminWithdrawalId = null;
                const updated = action.payload;
                const idx = draft.adminWithdrawals.findIndex(w => w.id === updated.id);
                if (idx >= 0) draft.adminWithdrawals[idx] = updated;
                break;
            }
            case types.PATCH_ADMIN_LOYALTY_WITHDRAWAL_FAIL:
                draft.patchingAdminWithdrawalId = null;
                break;

            case types.ADMIN_GRANT_LOYALTY:
                draft.grantingLoyalty = true;
                draft.adminGrantResult = null;
                break;
            case types.ADMIN_GRANT_LOYALTY_SUCCESS:
                draft.grantingLoyalty = false;
                draft.adminGrantResult = action.payload;
                break;
            case types.ADMIN_GRANT_LOYALTY_FAIL:
                draft.grantingLoyalty = false;
                break;
            case types.CLEAR_ADMIN_GRANT_LOYALTY:
                draft.adminGrantResult = null;
                break;

            case types.GET_ADMIN_LOYALTY_OVERVIEW:
                draft.fetchingAdminOverview = true;
                break;
            case types.GET_ADMIN_LOYALTY_OVERVIEW_SUCCESS:
                draft.fetchingAdminOverview = false;
                draft.adminOverview = action.payload;
                break;
            case types.GET_ADMIN_LOYALTY_OVERVIEW_FAIL:
                draft.fetchingAdminOverview = false;
                break;

            case types.GET_ADMIN_LOYALTY_LEADERBOARD:
                draft.fetchingAdminLeaderboard = true;
                break;
            case types.GET_ADMIN_LOYALTY_LEADERBOARD_SUCCESS:
                draft.fetchingAdminLeaderboard = false;
                draft.adminLeaderboard = action.payload;
                break;
            case types.GET_ADMIN_LOYALTY_LEADERBOARD_FAIL:
                draft.fetchingAdminLeaderboard = false;
                break;

            case types.GET_ADMIN_LOYALTY_USER:
                draft.fetchingAdminUser = true;
                draft.adminUserError = null;
                break;
            case types.GET_ADMIN_LOYALTY_USER_SUCCESS:
                draft.fetchingAdminUser = false;
                draft.adminUserDetail = action.payload;
                break;
            case types.GET_ADMIN_LOYALTY_USER_FAIL:
                draft.fetchingAdminUser = false;
                draft.adminUserError = action.payload;
                break;
            case types.CLEAR_ADMIN_LOYALTY_USER:
                draft.adminUserDetail = null;
                draft.adminUserError = null;
                break;

            default:
                break;
        }
    });
}
